import { MixpanelEvent, MixpanelEventProperty, MixpanelEventSource, useMixpanel } from '@talos/kyoko';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { useMobileModal } from '../../../../providers/MobileModalProvider';
import type { NavigationItem } from '../../../../tokens/navigationPaths';
import { OverflowModal } from '../OverflowModal';
import { TabBarAccessories } from '../TabBarAccessories';
import {
  SubSelectedIndicatorWrapper,
  SubTabBarWrapper,
  SubTabButtonElement,
  SubTabButtonLabel,
  SubTabButtonWrapper,
  SubTabNavLinkElement,
  SubTabOverflowButtonElement,
} from './styles';

const MOBILE_SUB_TAB_BAR_HORIZONTAL_SPACING = 4;
const MAX_ITEMS_WITHOUT_OVERFLOW = 4;
const VISIBLE_ITEMS_WITH_OVERFLOW = 3;

export function SubTabBar({ parent }: { parent: NavigationItem }) {
  // Filter out structural children (label undefined); they are routing-only and should not appear in the UI.
  const children = useMemo(
    () => (parent.children ?? []).filter((child: NavigationItem) => child.label != null),
    [parent.children]
  );
  const parentKey = getNavItemKey(parent);
  const storageKey = `mobile-sub-tab-bar-order:${parentKey}`;
  const [savedItemsOrder, setSavedItemsOrder] = useLocalStorage<string[]>(storageKey, []);

  const orderedItems = useMemo(
    () => sortItemsBySavedOrder(children, savedItemsOrder ?? []),
    [children, savedItemsOrder]
  );

  const hasOverflowItem = orderedItems.length > MAX_ITEMS_WITHOUT_OVERFLOW;
  const visibleItems = hasOverflowItem ? orderedItems.slice(0, VISIBLE_ITEMS_WITH_OVERFLOW) : orderedItems;
  const overflowCount = hasOverflowItem ? orderedItems.length - visibleItems.length : 0;
  const overflowLabel = `+${overflowCount}`;

  const location = useLocation();
  const selectedVisibleIndex = visibleItems.findIndex(item => item.path === location.pathname);
  const selectedIndex =
    hasOverflowItem && selectedVisibleIndex === -1
      ? orderedItems.some(item => item.path === location.pathname)
        ? visibleItems.length
        : -1
      : selectedVisibleIndex;

  const barRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLButtonElement>(null);
  const [tabButtonWidth, setTabButtonWidth] = useState(0);
  const [overflowButtonWidth, setOverflowButtonWidth] = useState(0);

  useLayoutEffect(() => {
    const innerBarWidth = (barRef.current?.offsetWidth ?? 0) - MOBILE_SUB_TAB_BAR_HORIZONTAL_SPACING * 2;
    const nextOverflowWidth = hasOverflowItem ? (overflowRef.current?.offsetWidth ?? 0) : 0;
    const remainingWidth = Math.max(innerBarWidth - nextOverflowWidth, 0);
    setOverflowButtonWidth(nextOverflowWidth);
    setTabButtonWidth(visibleItems.length > 0 ? remainingWidth / visibleItems.length : 0);
  }, [barRef.current?.offsetWidth, hasOverflowItem, visibleItems.length, overflowLabel]);

  const indicatorWidth = selectedIndex === visibleItems.length ? overflowButtonWidth : tabButtonWidth;
  const indicatorOffset =
    selectedIndex === visibleItems.length ? visibleItems.length * tabButtonWidth : selectedIndex * tabButtonWidth;

  const mixpanel = useMixpanel(MixpanelEventSource.MobileTabBar);

  const { openModal, closeModal, isModalOpen } = useMobileModal();

  const handleItemsChange = useCallback(
    (nextItems: NavigationItem[]) => {
      setSavedItemsOrder(nextItems.map(getNavItemKey));
    },
    [setSavedItemsOrder]
  );

  const trackNavigateToItem = useCallback(
    (item: NavigationItem) => {
      if (item.path == null) {
        return;
      }
      mixpanel.track(MixpanelEvent.NavigateTo, { [MixpanelEventProperty.Path]: item.path });
      if (item.mixpanelEvent) {
        mixpanel.track(item.mixpanelEvent);
      }
    },
    [mixpanel]
  );

  const handleOpenOverflow = useCallback(() => {
    if (isModalOpen) {
      closeModal();
      return;
    }
    openModal({
      content: <OverflowModal items={orderedItems} onItemsChange={handleItemsChange} title={parent.label} />,
    });
    mixpanel.track(MixpanelEvent.OpenMobileTabBarOverflow);
  }, [openModal, closeModal, isModalOpen, orderedItems, handleItemsChange, parent.label, mixpanel]);

  if (children.length === 0) {
    return null;
  }

  return (
    <TabBarAccessories>
      <SubTabBarWrapper ref={barRef} data-testid="mobile-sub-tab-bar">
        {selectedIndex >= 0 && (
          <SubSelectedIndicatorWrapper w={indicatorWidth} transform={`translateX(${indicatorOffset}px)`} />
        )}
        {visibleItems.map((item, index) => (
          <SubTabButton
            key={getNavItemKey(item)}
            item={item}
            isSelected={index === selectedIndex}
            onPress={() => trackNavigateToItem(item)}
          />
        ))}
        {hasOverflowItem && (
          <SubTabOverflowButtonElement ref={overflowRef} type="button" onClick={handleOpenOverflow}>
            <SubTabButtonWrapper isSelected={selectedIndex === visibleItems.length}>
              <SubTabButtonLabel data-testid="mobile-sub-tab-overflow-label">{overflowLabel}</SubTabButtonLabel>
            </SubTabButtonWrapper>
          </SubTabOverflowButtonElement>
        )}
      </SubTabBarWrapper>
    </TabBarAccessories>
  );
}

function SubTabButton({
  item,
  isSelected,
  onPress,
}: {
  item: NavigationItem;
  isSelected: boolean;
  onPress?: () => void;
}) {
  const content = (
    <SubTabButtonWrapper isSelected={isSelected}>
      <SubTabButtonLabel data-testid={getSubTabLabelTestId(item)}>{item.label}</SubTabButtonLabel>
    </SubTabButtonWrapper>
  );

  if (item.path == null) {
    return (
      <SubTabButtonElement type="button" onClick={onPress}>
        {content}
      </SubTabButtonElement>
    );
  }

  return (
    <SubTabNavLinkElement key={item.id} to={item.path} onClick={onPress}>
      {content}
    </SubTabNavLinkElement>
  );
}

function getNavItemKey(item: NavigationItem) {
  return item.id ?? item.path ?? '';
}

function getSubTabLabelTestId(item: NavigationItem) {
  return `mobile-sub-tab-label-${getNavItemKey(item)}`;
}

function sortItemsBySavedOrder(items: NavigationItem[], savedItemsOrder: string[]) {
  if (savedItemsOrder.length === 0) {
    return items;
  }

  const positions = new Map(savedItemsOrder.map((id, index) => [id, index]));
  return items.toSorted((a, b) => {
    const positionA = positions.get(getNavItemKey(a)) ?? Number.MAX_SAFE_INTEGER;
    const positionB = positions.get(getNavItemKey(b)) ?? Number.MAX_SAFE_INTEGER;
    return positionA - positionB;
  });
}
